/**
 * Admin i18n — 중국어 단일 (zh)
 *
 * 용어 기준: 腾讯云 / 阿里云 / 字节火山引擎 / 百度智能云 콘솔 표준 어휘
 *
 * 사용법:
 *   const { t } = useI18n();
 *   t('nav.metrics')                            // → "运营概况"
 *   t('pairing.remaining', { time: '9:30' })    // 파라미터 치환 {key}
 */

import { useSyncExternalStore } from 'react';

// zh 단일 고정 — 타입·시그니처는 기존 소비자와 호환 유지
export type Lang = 'zh';

const DEFAULT_LANG: Lang = 'zh';

// currentLang 는 항상 'zh'; setLang 호출 시에도 변경 없음
let currentLang: Lang = DEFAULT_LANG;

const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};
const getSnapshot = () => currentLang;

// 시그니처 유지 — no-op (zh 고정)
export function setLang(_next: Lang) {
  // 단일 언어 모드: 변경 없음
}

type Dict = Record<string, string>;

// ---------------------------------------------------------------------------
// zh 사전 — 중국 빅테크 콘솔 표준 용어
// 腾讯云·阿里云·字节火山引擎·百度智能云 어휘 기준
// ---------------------------------------------------------------------------
const zh: Dict = {
  // common — 공통 액션·레이블
  'common.save': '保存',
  'common.cancel': '取消',
  'common.delete': '删除',
  'common.close': '关闭',
  'common.copy': '复制',
  'common.copied': '已复制',
  'common.refresh': '刷新',
  'common.refreshing': '刷新中',
  'common.loading': '加载中…',
  'common.search': '搜索',
  'common.searching': '搜索中',
  'common.issue': '签发',
  'common.issuing': '签发中...',
  'common.confirming': '确认中...',
  'common.creating': '创建中...',
  'common.sending': '重发中...',
  'common.yes': '确定',
  'common.no': '取消',
  'common.none': '无',
  'common.actions': '操作',
  'common.status': '状态',
  'common.email': '邮箱',
  'common.name': '姓名',
  'common.displayName': '显示名称',
  'common.role': '角色',
  'common.note': '备注',
  'common.detail': '详情',
  'common.remove': '移除',
  'common.empty': '暂无数据',
  'common.dash': '-',
  'common.system': '系统',
  'common.unnamed': '（未命名）',
  'common.seed': '种子',
  'common.expired': '已过期',
  'common.expires': '到期时间',
  'common.sent': '已发送',
  'common.joined': '已加入',
  'common.signedUp': '已注册',
  'common.lastLogin': '最近登录',
  'common.lastUsed': '最近使用',
  'common.lastActive': '最近活跃',
  'common.lastSeen': '最近在线',
  'common.locale': '语言',
  'common.verified': '已认证',
  'common.workspace': '工作区',
  'common.workspaceId': '工作区编号',
  'common.workspaceSlug': '工作区标识符',
  'common.user': '用户',
  'common.userId': '用户编号',
  'common.device': '设备',
  'common.prefix': '前缀',
  'common.slug': '标识符',
  'common.telegramAccount': '飞机账号',
  'common.telegramChatId': '飞机聊天编号',
  'common.historyData': '历史数据',
  'common.member': '成员',
  'common.plan': '套餐',
  'common.created': '创建时间',
  'common.errCopy': '复制失败，请手动选择。',
  'common.errOtpResent': '验证码已重新发送。',

  // nav — 导航·侧栏
  'nav.brand': 'SayKnowWork',
  'nav.console': '管理控制台',
  'nav.groupOps': '服务运营',
  'nav.groupWorkspace': '当前工作区',
  'nav.platformMetrics': '运营概览',
  'nav.platformSignup': '开通账号',
  'nav.platformAccess': '注册审核',
  'nav.platformUsers': '用户管控',
  'nav.platformTenants': '工作区管理',
  'nav.platformAgents': '实时连接',
  'nav.activity': '操作日志',
  'nav.pairing': '配对码',
  'nav.devices': '接口密钥 / 设备',
  'nav.team': '成员 · 邀请',
  'nav.logout': '退出登录',
  'nav.operations': '运营状态',
  'nav.monitoring': '监控',
  'nav.devicesGroup': '设备',
  'nav.members': '用户账号',
  'nav.access': '注册审核',
  'nav.workspaces': '工作区',
  'nav.settings': '系统设置',

  // tabs — 标签页
  'tabs.metrics': '系统指标',
  'tabs.telegram': '飞机',
  'tabs.agents': '实时连接',
  'tabs.activity': '操作日志',
  'tabs.access': '注册审核',
  'tabs.signup': '新建工作区',
  'tabs.users': '用户账号',
  'tabs.team': '团队成员',
  'tabs.tenants': '工作区',
  'tabs.pairing': '配对码',
  'tabs.apikeys': '接口密钥',
  'tabs.devices': '接口密钥 / 设备',

  // login — 登录页
  'login.title': '管理控制台登录',
  'login.emailPh': '邮箱',
  'login.passwordPh': '密码',
  'login.submit': '登录',
  'login.remember': '记住我',
  'login.errRoleNotAllowed': '管理控制台仅限所有者或管理员访问。当前角色：{role}',
  'login.help': '仅所有者或管理员可登录。普通用户注册请登录后在「注册审核」中处理。',

  // theme — 主题切换
  'theme.light': '切换为浅色模式',
  'theme.dark': '切换为深色模式',

  // access.preregister — 预登记区块
  'access.preregister.section': '用户预登记',
  'access.preregister.sectionDesc': '通过工作区编号 + 邮箱直接预登记，或批量上传表格文件。登记条目将显示在下方队列中。',
  'access.preregister.workspaceNamePlaceholder': '工作区名称',
  'access.preregister.passwordPlaceholder': '初始密码（至少 8 位）',
  'access.preregister.createBtn': '新建工作区 + 用户',

  // access.queue — 审核队列
  'access.queue.section': '注册审核队列',
  'access.queue.sectionDesc': '运营预登记与用户注册申请统一审核。',
  'access.queue.col.source': '来源',
  'access.queue.col.signupInfo': '注册信息',
  'access.queue.col.workspaceRole': '工作区 / 角色',
  'access.queue.field.employeeNumber': '工号',
  'access.queue.field.nickname': '昵称',
  'access.queue.field.tgName': '飞机名称',
  'access.queue.field.tgUsername': '飞机账号',
  'access.queue.field.referralCode': '邀请码',
  'access.queue.field.telegramChatId': '聊天编号',
  'access.queue.searchPlaceholder': '搜索邮箱、工号、昵称、飞机账号、邀请码、工作区',
  'access.queue.filterStatus': '审核状态筛选',
  'access.queue.sortLabel': '排序方式',
  'access.queue.filter.all': '全部状态',
  'access.queue.sort.newest': '最新优先',
  'access.queue.sort.oldest': '最早优先',
  'access.queue.sort.status': '审核优先',
  'access.queue.reset': '重置',
  'access.queue.resultCount': '显示 {shown} / {total}',
  'access.queue.stat.total': '全部',
  'access.queue.stat.filtered': '当前结果',
  'access.queue.filteredEmpty': '暂无符合条件的注册申请。',
  'access.queue.empty': '队列为空。',

  // access — 通用字段
  'access.source.preregister': '预登记',
  'access.source.application': '用户申请',
  'access.csvTemplate': '下载模板',
  'access.csvFromFile': '从文件导入',
  'access.csvFileError': '文件读取失败。',
  'access.otp.manual': '{email} 的验证码：{code}\n\n（飞机机器人未配置，未自动发送。请将此验证码直接告知用户，有效期 10 分钟。）',
  'access.otp.notDelivered': '{email} 的验证码已生成，但发送失败（{reason}）。请引导用户先添加飞机机器人。',
  'access.selectWorkspace': '选择工作区',

  // access.detail — 申请详情
  'access.detail.matchedUser': '匹配用户编号',
  'access.detail.approvedTenant': '已批准工作区编号',
  'access.detail.employeeNumber': '工号（注册时填写）',
  'access.detail.nickname': '昵称（注册时填写）',
  'access.detail.tgName': '飞机显示名称',
  'access.detail.referralCode': '邀请码（注册时填写）',
  'access.detail.displayName': '显示名称（昵称或飞机名称）',
  'access.detail.requestedWorkspace': '注册申请的工作区',
  'access.detail.approvedTenantName': '实际创建的工作区',
  'access.detail.approvedTenantSlug': '工作区标识符',
  'access.detail.signupHint': '注册时收集字段：飞机名称、飞机账号、昵称、工号、邀请码、密码。工号合成为邮箱；昵称与飞机名称合并为显示名称。',
  'access.detail.pairingTitle': '配对码',
  'access.detail.pairingDesc': '审批通过后，运营人员在此页面签发本地应用的配对码。',
  'access.grants.deprecatedNotice': '预登记（选项 D 历史数据）— 新流程为用户自助注册 + 管理员审批。',
  'access.detail.passwordTitle': '密码',
  'access.detail.passwordDesc': '不存储明文（argon2 单向哈希）。如用户忘记密码，点击下方按钮重置 — 所有现有会话将立即失效。',
  'access.detail.resetPasswordBtn': '重置密码',
  'access.prompt.resetPassword': '请输入 {email} 的新密码（至少 8 位）：',
  'access.prompt.passwordTooShort': '密码长度至少 8 位。',
  'access.prompt.passwordChanged': '{email} 的密码已重置。\n\n新密码：{pw}\n\n请直接告知用户并要求其登录后修改。所有原有会话已终止。',

  // access.title 等 — 主页面元数据
  'access.title': '注册审核',
  'access.desc': '在同一页面完成预登记、注册审批、飞机验证码重发及设备锁定 / 远程清除。',
  'access.statsPendingReview': '待审核',
  'access.statsPendingOtp': '待验证',
  'access.statsActiveDevices': '活跃设备',
  'access.statsRecentEvents': '近期事件',
  'access.tab.grants': '预登记',
  'access.tab.applications': '注册申请',
  'access.tab.devices': '设备',
  'access.tab.events': '使用事件',

  // access.grants — 预登记
  'access.grants.section': '预放行用户',
  'access.grants.sectionDesc': '按工作区编号提前登记允许加入的用户。',
  'access.grants.addBtn': '添加预登记',
  'access.grants.col.matched': '匹配用户',
  'access.grants.empty': '暂无预登记用户。',
  'access.grants.csvTitle': '表格批量预登记',
  'access.grants.csvHint': '请使用模板字段：邮箱、显示名称、角色、飞机账号、飞机聊天编号、备注、到期时间。',
  'access.grants.csvImport': '执行表格导入',
  'access.grants.resendOtp': '重发验证码',

  // access.apps — 注册申请
  'access.apps.section': '注册申请审核队列',
  'access.apps.sectionDesc': '未匹配预登记信息的用户在此审核、批准或拒绝。',
  'access.apps.col.applicant': '申请人',
  'access.apps.col.requestedWs': '申请工作区',
  'access.apps.col.reasonNote': '备注 / 原因',
  'access.apps.approve': '批准',
  'access.apps.reject': '拒绝',
  'access.apps.reopen': '重新审核',
  'access.apps.empty': '暂无注册申请。',

  // access.devices — 设备管控
  'access.devices.section': '设备 / 应用权限管控',
  'access.devices.sectionDesc': '立即阻止指定用户的应用访问，或发起远程清除。',
  'access.devices.lock': '锁定应用',
  'access.devices.revoke': '吊销访问',
  'access.devices.wipe': '远程清除',
  'access.devices.wipeReq': '发起远程清除',
  'access.devices.empty': '暂无已登记设备。',

  // access.events — 使用事件
  'access.events.section': '近期使用事件',
  'access.events.sectionDesc': '用户工作台及本地应用上报至服务器的最近 20 条使用记录。',
  'access.events.col.time': '时间',
  'access.events.col.source': '来源',
  'access.events.col.category': '类别',
  'access.events.col.event': '事件',
  'access.events.empty': '暂无使用事件记录。',

  // access.alert / prompt — 提示信息
  'access.alert.needTenantEmail': '请填写工作区编号与邮箱。',
  'access.alert.needWorkspaceEmailPassword': '请填写工作区名称、邮箱和密码。',
  'access.alert.passwordTooShort': '密码至少 8 位。',
  'access.alert.needTenantCsv': '请填写工作区编号与表格内容。',
  'access.alert.csvDone': '表格导入完成：成功 {inserted} 条，失败 {errors} 条',
  'access.prompt.approveTenant': '请输入要批准的工作区编号。留空则以申请工作区名称新建工作区。',
  'access.prompt.rejectReason': '请输入拒绝原因。',
  'access.prompt.rejectDefault': '不符合审核标准',
  'access.prompt.reopenReason': '请输入重新审核原因。',
  'access.prompt.reopenDefault': '补充信息后重新审核',
  'access.prompt.resendOtp': '为 {email} 重新发放验证码？',
  'access.prompt.appNote': '请输入申请审核备注。',
  'access.prompt.grantNote': '请输入预登记备注。',
  'access.prompt.deviceAction': '确认执行「{label}」？在线设备立即生效，离线设备将在下次连接时处理。',

  // pairing — 配对码
  'pairing.title': '签发配对码',
  'pairing.desc': '用于将本地应用接入当前工作区的 8 位一次性配对码，需在 10 分钟内使用。',
  'pairing.section': '本地应用接入',
  'pairing.sectionDesc': '将签发的配对码填入本地应用的「工作区连接」界面。',
  'pairing.issued': '已签发的配对码',
  'pairing.remaining': '剩余时间 {time}',
  'pairing.newCode': '签发新配对码',
  'pairing.step1': '启动 SayKnowWork 本地应用',
  'pairing.step2': '进入 设置 → 「工作区连接」，输入此配对码',
  'pairing.step3': '稍后连接状态即会激活',
  'pairing.emptyTitle': '尚未签发配对码。',
  'pairing.emptyDesc': '签发后的配对码 10 分钟内仅可使用一次。',
  'pairing.targetSelf': '本人(默认)',
  'pairing.targetHint': '为其他成员签发时,该码与配对后的设备将记入对方名下。',

  // devices — 接口密钥 / 设备
  'devices.title': '接口密钥 / 设备',
  'devices.desc': '当前工作区已签发的接口密钥与连接状态。跨工作区的设备管控请在「注册审核」中处理。',
  'devices.confirmRevoke': '确认吊销此接口密钥？已连接的本地应用或外部客户端将立即断开。',
  'devices.activeSection': '有效接口密钥（{n}）',
  'devices.activeDesc': '本地应用与服务器之间的连接凭证。',
  'devices.activeEmpty': '暂无有效接口密钥。请在「配对码」中签发新码以接入本地应用。',
  'devices.revoking': '吊销中...',
  'devices.revoke': '吊销密钥',
  'devices.revokedSection': '已吊销接口密钥（{n}）',
  'devices.revokedDesc': '这些密钥已无法使用。',
  'devices.revokedAt': '吊销于 {date}',

  // devices.admin — 系统级设备管控（platform admin）
  'devices.admin.title': '设备管控',
  'devices.admin.desc': '查看全平台所有工作区的已注册设备，执行吊销、锁定或远程清除。',
  'devices.admin.section': '全部设备（{n}）',
  'devices.admin.sectionDesc': '包含所有工作区的已配对本地应用。',
  'devices.admin.empty': '暂无设备记录。',
  'devices.col.deviceName': '设备名',
  'devices.col.platform': '平台',
  'devices.col.workspace': '工作区',
  'devices.col.apiKeyId': '接口密钥',
  'devices.searchPh': '搜索设备名、邮箱或工作区',
  'devices.filter.all': '全部状态',
  'devices.action.revoke': '吊销',
  'devices.action.lock': '锁定',
  'devices.action.wipe': '远程清除',
  'devices.action.confirm': '确认对此设备执行「{label}」？在线设备立即生效，离线设备将在下次连接时处理。',

  // team — 成员·邀请
  'team.title': '成员 · 邀请',
  'team.desc': '管理当前工作区的成员角色与邀请状态。',
  'team.inviteCreated': '邀请已创建，请查看邮件通知。',
  'team.inviteSection': '邀请成员',
  'team.inviteSectionDesc': '受邀用户将按角色获得对应的工作区权限。',
  'team.inviteBtn': '新建邀请',
  'team.membersSection': '成员（{n}）',
  'team.membersSectionDesc': '当前工作区内的用户。',
  'team.membersEmpty': '暂无成员。',
  'team.invitesSection': '待接受邀请（{n}）',
  'team.invitesSectionDesc': '尚未被接受的邀请。',
  'team.invitesEmpty': '暂无待接受邀请',
  'team.confirmRemove': '确认将 {email} 从此工作区移除？',
  'team.confirmCancelInvite': '确认取消此邀请？',

  // activity — 操作日志
  'activity.title': '操作日志',
  'activity.desc': '来自审计日志表的管理员操作与工作区关键变更记录。',
  'activity.section': '近期活动',
  'activity.sectionDesc': '按时间顺序查看目标对象、操作类型与原始审计动作。',
  'activity.empty': '暂无活动记录。',
  'activity.all': '全部',
  'activity.filterEmployee': '智能体',
  'activity.filterAutomation': '自动化任务',
  'activity.filterSoul': '知识库',
  'activity.filterInvite': '邀请',
  'activity.filterMember': '成员',
  'activity.filterConversation': '对话记录',
  'activity.verb.created': '创建',
  'activity.verb.updated': '编辑',
  'activity.verb.deleted': '删除',
  'activity.verb.ran': '执行',
  'activity.verb.accessed': '查看',
  'activity.target.employee': '智能体',
  'activity.target.automation': '自动化任务',
  'activity.target.soul': '知识库',
  'activity.target.invite': '邀请',
  'activity.target.member': '用户账号',
  'activity.target.conversation': '对话记录',
  'activity.target.tool': '工具',
  'activity.target.apiKey': '接口密钥',
  'activity.target.pairing': '配对',
  'activity.target.default': '对象',
  'activity.line.actorSuffix': ' ',
  'activity.line.systemSuffix': '',
  'activity.line.targetSuffix': '{target} ',
  'activity.time.justNow': '刚刚',
  'activity.time.minAgo': '{n} 分钟前',
  'activity.time.hourAgo': '{n} 小时前',

  // tenants — 工作区管理
  'tenants.title': '工作区管理',
  'tenants.desc': '管理全部工作区与应用使用策略。共 {n} 个',
  'tenants.section': '工作区列表',
  'tenants.sectionDesc': '查看套餐、离线宽限时长与关键数据规模。',
  'tenants.col.owner': '注册人 / 所有者',
  'tenants.col.signup': '注册审核信息',
  'tenants.col.requested': '申请名称',
  'tenants.source.direct': '直接创建',
  'tenants.col.offline': '离线宽限',
  'tenants.col.employees': '智能体数量',
  'tenants.col.conversations': '对话记录',
  'tenants.col.deletedAt': '删除时间',
  'tenants.empty': '暂无工作区。',
  'tenants.promptOffline': '请输入离线宽限时长（分钟）。0 为严格模式，最大 1440 分钟。',
  'tenants.alertOfflineRange': '请输入 0~1440 之间的整数（分钟）。',
  'tenants.minutes': '{n} 分钟',
  'tenants.offlineDesc': '网络中断时桌面端仍可使用的宽限时间。0 = 严格模式（断网立即拦截）。',
  'tenants.tab.active': '正常工作区',
  'tenants.tab.deleted': '已删除',
  'tenants.addWorkspace': '新建工作区',
  'tenants.addWorkspace.title': '新建工作区',
  'tenants.addWorkspace.name': '工作区名称',
  'tenants.addWorkspace.slug': '标识符（可选，留空自动生成）',
  'tenants.addWorkspace.submit': '创建',
  'tenants.addWorkspace.success': '工作区 {name} 已创建。',
  'tenants.searchPh': '搜索工作区名称或标识符',
  'tenants.confirmDelete': '确认软删除工作区「{name}」？可通过「恢复」操作撤销。',
  'tenants.confirmRestore': '确认恢复工作区「{name}」？',
  'tenants.action.softDelete': '删除',
  'tenants.action.restoreDeleted': '恢复',

  // agents — 实时连接
  'agents.title': '实时连接',
  'agents.desc': '当前通过实时通道接入的本地应用实例。活跃连接 {n} 个 · 工作区 {t} 个 · 每 10 秒刷新',
  'agents.section': '已连接的本地应用',
  'agents.sectionDesc': '同时查看连接时效与接口密钥维度。',
  'agents.col.apiKey': '接口密钥',
  'agents.empty': '当前暂无本地应用连接。',
  'agents.lastSec': '{n} 秒前',

  // signup — 开通账号
  'signup.title': '开通新账号',
  'signup.desc': '运营人员可直接为新工作区开通所有者账号，跳过飞机验证与注册申请步骤，账号即时生效。',
  'signup.section': '账号信息',
  'signup.field.tempPw': '临时密码',
  'signup.hint.tempPw': '至少 8 位。建议首次登录后提示用户修改。',
  'signup.hint.displayName': '留空时使用邮箱本地部分。',
  'signup.field.workspaceName': '工作区名称',
  'signup.role.owner': '所有者 — 工作区所有者',
  'signup.role.admin': '管理员 — 工作区管理员',
  'signup.reset': '重置',
  'signup.resultSection': '开通结果',
  'signup.resultAlert': '开通完成。请直接将邮箱与临时密码交付给本人。离开本页面后密码将无法再次显示。',
  'signup.resultEmpty': '在左侧表单填写新用户信息，结果将显示在此处。',
  'signup.row.userId': '用户编号',
  'signup.row.tenantId': '工作区编号',

  // metrics — 运营概览
  'metrics.title': '服务运营概览',
  'metrics.desc': '快速查看整体服务状态。最后更新：{ts} · 每 30 秒自动刷新',
  'metrics.section.active': '活跃用户',
  'metrics.section.activeDesc': '按登录活动查看服务使用趋势。',
  'metrics.dau': '日活跃用户',
  'metrics.wau': '周活跃用户',
  'metrics.mau': '月活跃用户',
  'metrics.dauSub': '24 小时内登录',
  'metrics.wauSub': '7 天内登录',
  'metrics.mauSub': '30 天内登录',
  'metrics.section.account': '账号状态',
  'metrics.section.accountDesc': '从注册到认证、首次登录的转化漏斗。',
  'metrics.totalAccounts': '账号总数',
  'metrics.verified': '已认证',
  'metrics.firstLogin': '首次登录',
  'metrics.section.service': '服务数据',
  'metrics.section.serviceDesc': '各工作区的累计数据。',
  'metrics.workspaces': '工作区',
  'metrics.employees': '智能体',
  'metrics.automations': '自动化任务',
  'metrics.conversations': '对话记录',
  'metrics.executions': '执行记录',

  // users — 用户管控
  'users.title': '用户管控',
  'users.desc': '查看账号状态、飞机绑定与工作区成员资格，执行权限吊销 / 恢复 / 删除。',
  'users.section': '用户搜索',
  'users.sectionDesc': '全部用户共 {n} 人',
  'users.searchPh': '按邮箱 / 姓名搜索',
  'users.col.referralCode': '邀请码',
  'users.col.referrer': '邀请人',
  'users.col.deletedAt': '删除时间',
  'users.confirmDelete': '确认软删除 {email} 账号？账号将被禁用，可通过「恢复」操作撤销。',
  'users.confirmRestore': '确认恢复 {email} 账号？账号将重新激活。',
  'users.tab.active': '正常用户',
  'users.tab.deleted': '已删除',
  'users.addUser': '添加用户',
  'users.addUser.title': '添加新用户',
  'users.addUser.email': '邮箱',
  'users.addUser.password': '密码（至少 8 位）',
  'users.addUser.displayName': '显示名称（可选）',
  'users.addUser.tenantId': '工作区编号（可选）',
  'users.addUser.role': '角色（可选）',
  'users.addUser.submit': '创建用户',
  'users.addUser.success': '用户 {email} 已创建。',
  'users.action.softDelete': '删除',
  'users.action.restoreDeleted': '恢复',
  'users.action.resetPassword': '重置密码',
  'users.detail.unnamed': '未命名',
  'users.detail.accountInfo': '账号信息',
  'users.detail.memberships': '工作区成员资格（{n}）',
  'users.detail.membershipsEmpty': '无',
  'users.detail.wipeWarning': '远程清除将先断开服务器数据与连接凭证，本地缓存将在设备联网或下次连接时清除。',
  'users.action.revoke': '吊销访问',
  'users.action.restore': '恢复访问',
  'users.action.eraseAndWipe': '删除服务器数据 + 远程清除',
  'users.action.deleteGdpr': '注销账号（合规删除）',
  'users.action.label.revoke': '吊销应用访问权限',
  'users.action.label.restore': '恢复应用访问权限',
  'users.action.label.erase': '删除服务器数据',
  'users.action.label.eraseWipe': '删除服务器数据并发起远程清除',
  'users.action.confirm': '确认对该用户执行「{label}」？离线设备将在下次连接时处理。',
  'users.deleted.emailErased': '（数据已抹除）',
  'users.deleted.deletedBy': '删除操作人',
  'users.deleted.type': '类型',
  'users.deleted.typeBadge.soft': '可恢复',
  'users.deleted.typeBadge.hard': '仅记录',

  // pager — 分页
  'pager.total': '{from}–{to} / 共 {total}',
  'pager.first': '« 首页',
  'pager.prev': '‹ 上一页',
  'pager.next': '下一页 ›',
  'pager.last': '末页 »',

  // status — 状态标签
  'status.active': '正常',
  'status.preapproved': '已预登记',
  'status.pending_review': '待审核',
  'status.pending_telegram': '待飞机验证',
  'status.matched_pending_telegram': '已匹配 · 待验证',
  'status.approved_pending_telegram': '已批准 · 待验证',
  'status.rejected': '已拒绝',
  'status.revoked': '已吊销',
  'status.deleted': '已删除',
  'status.locked': '已锁定',
  'status.wipe_pending': '远程清除待执行',

  // role — 角色
  'role.owner': '所有者',
  'role.admin': '管理员',
  'role.member': '普通成员',
  'role.viewer': '只读',

  // plan — 套餐
  'plan.free': '免费版',
  'plan.pro': '专业版',
  'plan.team': '团队版',
  'plan.enterprise': '企业版',
};

// ---------------------------------------------------------------------------
// 런타임 조회 함수
// ---------------------------------------------------------------------------

export function useI18n() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const t = (key: string, params?: Record<string, string | number>): string => {
    let s = zh[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return s;
  };
  return { lang, t, setLang };
}

// zh 단일 — LANGS 는 소비자 코드 호환을 위해 유지 (LangDropup 제거 후 미사용)
export const LANGS: { code: Lang; label: string }[] = [
  { code: 'zh', label: '中文' },
];
